import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import SessionModal from './SessionModal';
import StudentReviewModal from './StudentReviewModal';
import CommunicationLog from './CommunicationLog';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLogOut, FiUsers, FiCalendar, FiUser, FiMessageSquare, FiTrendingUp } = FiIcons;

const TutorDashboard = () => {
  const { user, logout } = useAuth();
  const { students, calculateTotalSessions, appSettings, studentReviews } = useData();
  const [activeTab, setActiveTab] = useState('students');
  const [sessionStudent, setSessionStudent] = useState(null);
  const [reviewStudent, setReviewStudent] = useState(null);

  const myStudents = students.filter(student => {
    if (student.subjects && Array.isArray(student.subjects)) {
      return student.subjects.some(sub => 
        sub.tutor === user?.firstName || 
        sub.tutor === user?.name || 
        sub.tutor === `${user?.firstName} ${user?.lastName}`
      );
    }
    return student.tutor === user?.firstName || student.tutor === user?.name || student.tutor === `${user?.firstName} ${user?.lastName}`;
  });

  const tutorStats = {
    totalStudents: myStudents.length,
    activeStudents: myStudents.filter(s => s.status === 'Active').length,
    totalSessions: myStudents.reduce((total, student) => total + calculateTotalSessions(student.id),0),
    totalReports: studentReviews.filter(r => r.tutorId === (user.id || user.uid)).length
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-emerald-50 text-emerald-600',
      'Trial': 'bg-amber-50 text-amber-600',
      'Idle': 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg" style={{ backgroundColor: appSettings.brandColor }}>
                <SafeIcon icon={FiUser} className="text-2xl" />
              </div>
              <div>
                <h1 className="text-xl font-black text-gray-900 tracking-tight">{appSettings.companyName}</h1>
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest">Tutor Portal • {user?.name}</p>
              </div>
            </div>
            <button onClick={logout} className="flex items-center gap-2 px-5 py-2.5 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl font-bold transition-all">
              <SafeIcon icon={FiLogOut} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {[
            { label: 'Assigned Students', value: tutorStats.totalStudents, icon: FiUsers, color: 'indigo' },
            { label: 'Active Learners', value: tutorStats.activeStudents, icon: FiTrendingUp, color: 'emerald' },
            { label: 'Total Classes', value: tutorStats.totalSessions, icon: FiCalendar, color: 'blue' },
            { label: 'Session Reports', value: tutorStats.totalReports, icon: FiMessageSquare, color: 'purple' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100">
              <div className={`w-12 h-12 rounded-2xl bg-${stat.color}-50 text-${stat.color}-600 flex items-center justify-center mb-6`}>
                <SafeIcon icon={stat.icon} className="text-xl" />
              </div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{stat.label}</p>
              <h3 className="text-3xl font-black text-gray-900">{stat.value}</h3>
            </div>
          ))}
        </div>

        <div className="flex gap-4 mb-10">
          <button onClick={() => setActiveTab('students')} className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'students' ? 'bg-indigo-600 text-white shadow-xl' : 'bg-white text-gray-400 border border-gray-100'}`}>
            <SafeIcon icon={FiUsers} /> Student Roster
          </button>
          <button onClick={() => setActiveTab('comms')} className={`flex items-center gap-2 px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'comms' ? 'bg-indigo-600 text-white shadow-xl' : 'bg-white text-gray-400 border border-gray-100'}`}>
            <SafeIcon icon={FiMessageSquare} /> Communication Log
          </button>
        </div>

        {activeTab === 'students' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {myStudents.map(student => (
              <motion.div whileHover={{ y: -5 }} key={student.id} className="bg-white p-8 rounded-[3rem] shadow-sm border border-transparent hover:border-indigo-100 transition-all group">
                <div className="flex justify-between items-start mb-6">
                  <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                    <SafeIcon icon={FiUser} className="text-2xl" />
                  </div>
                  <span className={`text-[10px] font-black px-4 py-1.5 rounded-full tracking-widest uppercase ${getStatusColor(student.status)}`}>
                    {student.status}
                  </span>
                </div>
                <h3 className="text-2xl font-black text-gray-900 mb-1">{student.studentName}</h3>
                <p className="text-xs font-bold text-gray-400 mb-8 uppercase tracking-widest">Grade {student.grade}</p>
                
                <div className="flex gap-3">
                  <button 
                    onClick={() => setSessionStudent(student)}
                    className="flex-1 bg-gray-900 text-white py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiCalendar} /> Manage Classes
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <CommunicationLog />
        )}

        {sessionStudent && <SessionModal student={sessionStudent} onClose={() => setSessionStudent(null)} />}
      </main>
    </div>
  );
};

export default TutorDashboard;